import { resolveAnnotationSessionEndpoint } from './apiConfig';

// Musical time constants
export const SUBDIVISIONS_PER_BEAT = 4; // Default fallback
export const DEFAULT_PIXELS_PER_BEAT = 40;

// Time signature utility functions
export const parseTimeSignature = (timeSignature) => {
  if (!timeSignature || typeof timeSignature !== 'string') {
    return { numerator: 4, denominator: 4 }; // Default to 4/4
  }
  
  const parts = timeSignature.split('/');
  if (parts.length !== 2) {
    return { numerator: 4, denominator: 4 }; // Default to 4/4
  }
  
  const numerator = parseInt(parts[0], 10);
  const denominator = parseInt(parts[1], 10);
  
  if (isNaN(numerator) || isNaN(denominator)) {
    return { numerator: 4, denominator: 4 }; // Default to 4/4
  }
  
  return { numerator, denominator };
};

// Calculate subdivisions per beat based on denominator (16th note as smallest unit)
export const getSubdivisionsPerBeat = (denominator) => {
  if (denominator >= 16) {
    return 0; // No subdivisions for 16th notes or smaller
  }
  return Math.floor(16 / denominator);
};

// Get subdivisions for a specific measure
export const getSubdivisionsForMeasure = (timeSignatures, measureIndex) => {
  if (!timeSignatures || measureIndex < 0 || measureIndex >= timeSignatures.length) {
    return SUBDIVISIONS_PER_BEAT; // Default fallback
  }
  
  const { denominator } = parseTimeSignature(timeSignatures[measureIndex]);
  return getSubdivisionsPerBeat(denominator);
};

// Grid level thresholds
export const GRID_LEVEL_THRESHOLDS = {
  SUBDIVISION_ZOOM_MIN: 1.5,  // Minimum zoom to show subdivisions
  BEAT_VISIBILITY_MAX: 80,    // Maximum beats visible before hiding beat lines (20 bars * 4 beats)
};

// Grid level types
export const GRID_LEVELS = {
  BAR: 'bar',
  BEAT: 'beat', 
  SUBDIVISION: 'subdivision'
};

// Zoom constants
export const ZOOM_RANGE = {
  HORIZONTAL: {
    MIN: 0.1,  // Lowered to allow more zoom out
    MAX: 4,
    STEP: 0.1
  },
  VERTICAL: {
    MIN: 0.3,
    MAX: 1.2,  // Reduced from 3.0 to prevent excessive zoom-in
    STEP: 0.1
  }
};

// Visual constants
export const DEFAULT_BLOCK_HEIGHT = 80;
export const DEFAULT_FONT_SIZE = 12;
export const DEFAULT_STROKE_WIDTH = 1;

// Colors
export const COLORS = {
  // Sidebar colors
  SIDEBAR_USER: "#000000",
  SIDEBAR_ADMIN: "#4B5872",

  // Background colors
  BG_LIGHT: "#F6F6F6",
  BG_HOVER: "#f0f0f0",
  BG_ACTIVE: "#e5e5e5",
  
  // Divider colors
  DIVIDER: "#00000080",
  
  // Block colors
  BLOCK_SELECTED: "#ddd",
  BLOCK_DEFAULT: "#eee",
  BLOCK_BORDER: "#000",
  
  // Text colors
  ANNOTATION_TEXT: "#000",
  TEXT_SECONDARY: "#666",
  
  // UI element colors
  GRID_LINE: "#ddd",
  MEASURE_LINE: "#666",
  BEAT_LINE: "#ddd",
  
  // Interactive element colors
  BUTTON_HOVER: "#f5f5f5",
  BUTTON_ACTIVE: "#e0e0e0",
  
  // Status colors
  ERROR: "#ff0000",
  WARNING: "#ffa500",
  SUCCESS: "#00ff00",
  
  // Track colors
  TRACK_CHORD: "#79B084",     // Green for Chord Track
  TRACK_PIANO: "#BF4C93",     // Pink/Purple for Piano Instrument Track
  TRACK_PAD: "#5E75A4",       // Blue for Pad Instrument Track
  TRACK_DRUM: "#EA8451",      // Orange for Drum Instrument Track
  
  // Bar Level Annotation track colors
  BAR_LEVEL_COLORS: [
    "#4E8C5D", "#A23F7C", "#BF6732", "#42C0D1", "#3A4F80",
    "#CE939A", "#B42435", "#C8CB00", "#79B084", "#BF4C93", 
    "#EA8451", "#42C0D1", "#5E75A4", "#F2B1B8", "#E16776", 
    "#FFCB53", "#A3C7A0", "#D97DB2", "#F5A873", "#42C0D1", 
    "#8295C0", "#EBBCC1", "#EC7D64", "#FFDA62"
  ]
};

export const AWS_LAMBDA_ENDPOINT = process.env.REACT_APP_AWS_LAMBDA_ENDPOINT
export const ANNOTATION_SESSION_ENDPOINT = resolveAnnotationSessionEndpoint(process.env)
export const ALB_API_BASE_URL = process.env.REACT_APP_API_BASE_ENDPOINT
// Unknown track detection utility
export const isUnknownTrack = (trackName) => {
  if (!trackName || typeof trackName !== 'string') {
    return false;
  }
  
  const name = trackName.toLowerCase().trim();
  
  // Check for exact match with "unknown"
  if (name === 'unknown') {
    return true;
  }
  
  // Check for unknown track patterns
  return name.includes('unknown instrument') || 
         name.includes('unknown');
};